import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useCreateTenant, useSendInvite } from "@/hooks/api/useAdminApi";
import { ArrowLeft, Copy, Mail, CheckCircle } from "lucide-react";

export default function CreateTenant() {
  const navigate = useNavigate();
  const createMutation = useCreateTenant();
  const sendInviteMutation = useSendInvite();

  const [formData, setFormData] = useState({
    companyName: "",
    slug: "",
    ownerEmail: "",
    initialMonthlyCredits: "",
    initialAddonCredits: "",
  });

  const [createdTenant, setCreatedTenant] = useState<{
    id: string;
    inviteLink: string;
  } | null>(null);

  const handleCompanyNameChange = (name: string) => {
    setFormData({
      ...formData,
      companyName: name,
      slug: name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, ""),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.companyName || !formData.slug || !formData.ownerEmail) {
      toast.error("Please fill in all required fields");
      return;
    }

    const payload: any = {
      companyName: formData.companyName,
      slug: formData.slug,
      ownerEmail: formData.ownerEmail,
    };

    if (formData.initialMonthlyCredits) {
      payload.initialMonthlyCredits = parseInt(formData.initialMonthlyCredits);
    }

    if (formData.initialAddonCredits) {
      payload.initialAddonCredits = parseInt(formData.initialAddonCredits);
    }

    createMutation.mutate(payload, {
      onSuccess: (data) => {
        toast.success("Tenant created successfully");
        setCreatedTenant({
          id: data.id,
          inviteLink: data.inviteLink,
        });
      },
      onError: (error: any) => {
        toast.error(error.message || "Failed to create tenant");
      },
    });
  };

  const handleCopyLink = () => {
    if (createdTenant?.inviteLink) {
      navigator.clipboard.writeText(createdTenant.inviteLink);
      toast.success("Invite link copied to clipboard");
    }
  };

  const handleSendInvite = () => {
    if (!createdTenant) return;

    sendInviteMutation.mutate(
      { tenantId: createdTenant.id, email: formData.ownerEmail },
      {
        onSuccess: () => {
          toast.success("Invite email sent");
        },
        onError: () => {
          toast.error("Failed to send invite email");
        },
      }
    );
  };

  if (createdTenant) {
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/tenants")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Tenant Created</h1>
            <p className="text-muted-foreground">Successfully created tenant account</p>
          </div>
        </div>

        <Card className="border-green-500 bg-green-50 dark:bg-green-950">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Tenant Created Successfully
            </CardTitle>
            <CardDescription>Share the invite link or send an email to the owner</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Invite Link</Label>
              <div className="flex gap-2">
                <Input value={createdTenant.inviteLink} readOnly className="bg-white dark:bg-gray-900" />
                <Button onClick={handleCopyLink} variant="outline">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSendInvite} disabled={sendInviteMutation.isPending}>
                <Mail className="h-4 w-4 mr-2" />
                {sendInviteMutation.isPending ? "Sending..." : "Send Invite Email"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button onClick={() => navigate("/admin/tenants")} variant="outline">
            Back to Tenant List
          </Button>
          <Button onClick={() => navigate(`/admin/tenants/${createdTenant.id}`)}>
            View Tenant Details
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/tenants")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Create Tenant</h1>
          <p className="text-muted-foreground">Set up a new contractor account</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tenant Information</CardTitle>
          <CardDescription>Fill in the details for the new tenant</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="companyName">
                Company Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="companyName"
                value={formData.companyName}
                onChange={(e) => handleCompanyNameChange(e.target.value)}
                placeholder="ABC Contractors"
                required
              />
            </div>

            <div>
              <Label htmlFor="slug">
                Slug <span className="text-destructive">*</span>
              </Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="abc-contractors"
                required
              />
              <p className="text-sm text-muted-foreground mt-1">
                Used in widget URL: /widget/{formData.slug || "slug"}
              </p>
            </div>

            <div>
              <Label htmlFor="ownerEmail">
                Owner Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ownerEmail"
                type="email"
                value={formData.ownerEmail}
                onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
                placeholder="owner@example.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="initialMonthlyCredits">Initial Monthly Credits</Label>
              <Input
                id="initialMonthlyCredits"
                type="number"
                value={formData.initialMonthlyCredits}
                onChange={(e) => setFormData({ ...formData, initialMonthlyCredits: e.target.value })}
                placeholder="0"
                min="0"
              />
              <p className="text-sm text-muted-foreground mt-1">Monthly credit allocation</p>
            </div>

            <div>
              <Label htmlFor="initialAddonCredits">Initial Addon Credits</Label>
              <Input
                id="initialAddonCredits"
                type="number"
                value={formData.initialAddonCredits}
                onChange={(e) => setFormData({ ...formData, initialAddonCredits: e.target.value })}
                placeholder="0"
                min="0"
              />
              <p className="text-sm text-muted-foreground mt-1">One-time addon credits</p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate("/admin/tenants")}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create Tenant"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
